import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { formatINR } from "@/lib/sections";
import { X, ExternalLink, Check, Ban, CheckCircle2, Undo2 } from "lucide-react";
import HallRatesEditor from "@/components/admin/HallRatesEditor";
import GalleryCalendar from "@/components/admin/GalleryCalendar";
import ApprovalSchedule, { HALL_KEYS, countWorkingDays, slotDates } from "@/components/admin/ApprovalSchedule";

const STATUS_COLORS = {
  submitted:   "#5C5C55",
  shortlisted: "#7A6F3C",
  approved:    "#4A5D4E",
  paid:        "#4A5D4E",
  rejected:    "#8B2A2A",
  cancelled:   "#5C5C55",
};

function formatSlot(id, slots) {
  const s = slots.find((x) => x.id === id);
  if (!s) return id || "—";
  const opts = { day: "numeric", month: "short", year: "numeric" };
  const a = new Date(s.start_date + "T00:00:00").toLocaleDateString("en-IN", opts);
  const b = new Date(s.end_date + "T00:00:00").toLocaleDateString("en-IN", opts);
  return `${a} → ${b}`;
}

export default function AdminGalleryApplications() {
  const [apps, setApps] = useState([]);
  const [options, setOptions] = useState({});
  const [slots, setSlots] = useState([]);
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCalendar, setShowCalendar] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/gallery-applications"),
      api.get("/gallery/options"),
      api.get("/gallery/slots", { params: { months: 14 } }),
      api.get("/admin/gallery/hall-rates"),
    ]).then(([a, o, s, r]) => {
      setApps(a.data);
      setOptions(o.data.options);
      setSlots(s.data.slots);
      setRates(Object.fromEntries(r.data.halls.map((h) => [h.key, h.rate])));
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(
    () => statusFilter === "all" ? apps : apps.filter((a) => a.status === statusFilter),
    [apps, statusFilter]
  );

  const setStatus = async (id, patch) => {
    try {
      const res = await api.put(`/admin/gallery-applications/${id}`, patch);
      toast.success(patch.status === "paid" ? "Marked paid — exhibition added to Upcoming Events" : "Updated");
      if (res.data.conflicts?.length) toast.warning(`Approved with ${res.data.conflicts.length} overlapping booking(s)`);
      const fresh = (await api.get(`/admin/gallery-applications/${id}`)).data;
      setApps((xs) => xs.map((x) => (x.id === id ? fresh : x)));
      if (selected?.id === id) setSelected(fresh);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Failed"));
    }
  };

  return (
    <div data-testid="admin-gallery-applications">
      <div className="overline">Gallery</div>
      <h1 className="display text-5xl mt-3">Exhibition applications.</h1>

      <HallRatesEditor onSaved={load} />

      <div className="mt-8 flex items-center justify-between">
        <div className="overline">Hall calendar</div>
        <button onClick={() => setShowCalendar((v) => !v)} className="text-xs link-hover" data-testid="calendar-toggle">
          {showCalendar ? "Hide calendar" : "Show calendar"}
        </button>
      </div>
      <div className={showCalendar ? "" : "hidden"}>
        <GalleryCalendar refreshKey={apps} onOpen={async (id) => {
          const a = apps.find((x) => x.id === id) || (await api.get(`/admin/gallery-applications/${id}`)).data;
          setSelected(a);
        }} />
      </div>

      <div className="mt-8 flex gap-2 flex-wrap">
        {["all","submitted","shortlisted","approved","paid","rejected","cancelled"].map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-2 border ${statusFilter === s ? "bg-charcoal text-cream border-charcoal" : "border-soft hover:bg-cream-2"}`}
            style={statusFilter === s ? { background: "#1C1C19", color: "#F9F8F6" } : {}}
            data-testid={`status-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="mt-8 border border-soft bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="text-left text-xs uppercase tracking-widest text-muted2">
            <tr>
              <th className="p-4">Ref</th>
              <th className="p-4">Artist</th>
              <th className="p-4">Space</th>
              <th className="p-4">Preferred slot</th>
              <th className="p-4">Status</th>
              <th className="p-4">Submitted</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-8 text-muted2">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-muted2">No applications yet.</td></tr>}
            {filtered.map((a) => (
              <tr key={a.id} className="border-t border-soft" data-testid={`app-row-${a.reference}`}>
                <td className="p-4 font-mono text-xs">{a.reference}</td>
                <td className="p-4">
                  <div className="font-serif text-base">{a.artist_name}</div>
                  <div className="text-xs text-muted2">{a.email}</div>
                </td>
                <td className="p-4 text-xs">{options[a.preferred_option]?.label || a.preferred_option}</td>
                <td className="p-4 text-xs">{formatSlot(a.preferred_slot_ids?.[0], slots)}</td>
                <td className="p-4 text-xs" style={{color: STATUS_COLORS[a.status] || "inherit"}}>{a.status}</td>
                <td className="p-4 text-xs">{new Date(a.created_at).toLocaleDateString("en-IN")}</td>
                <td className="p-4 text-right">
                  <button onClick={() => setSelected(a)} className="text-xs link-hover" data-testid={`app-open-${a.reference}`}>Open →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <ApplicationDrawer
          app={selected}
          options={options}
          slots={slots}
          rates={rates}
          onClose={() => setSelected(null)}
          onUpdate={(patch) => setStatus(selected.id, patch)}
        />
      )}
    </div>
  );
}

function ApplicationDrawer({ app, options, slots, rates: currentRates, onClose, onUpdate }) {
  const rates = app.hall_rates_snapshot || currentRates;
  const firstPref = slotDates(app.preferred_slot_ids?.[0]);
  const [halls, setHalls] = useState(app.assigned_halls?.length ? app.assigned_halls : (options[app.preferred_option]?.rooms || []));
  const [start, setStart] = useState(app.assigned_start_date || firstPref?.start || "");
  const [end, setEnd] = useState(app.assigned_end_date || firstPref?.end || "");
  const [conflicts, setConflicts] = useState([]);
  const [feeInput, setFeeInput] = useState(String(app.final_fee_inr || ""));
  const [feeTouched, setFeeTouched] = useState(!!app.calculated_fee_inr && app.final_fee_inr !== app.calculated_fee_inr);
  const [feeNote, setFeeNote] = useState(app.fee_note || "");
  const [notes, setNotes] = useState(app.admin_notes || "");

  const perDay = halls.reduce((sum, h) => sum + (rates[h] || 0), 0);
  const workingDays = countWorkingDays(start, end);
  const defaultFee = perDay * workingDays;
  const finalFee = feeInput.trim() === "" ? defaultFee : parseInt(feeInput, 10) || 0;
  const isOverride = feeTouched && feeInput.trim() !== "" && finalFee !== defaultFee;
  const canApprove = halls.length > 0 && workingDays > 0 && app.status !== "paid";

  useEffect(() => {
    if (!feeTouched) setFeeInput(defaultFee ? String(defaultFee) : "");
  }, [defaultFee, feeTouched]);

  const openFile = (url) => {
    if (!url) return;
    window.open(url.startsWith("http") ? url : (process.env.REACT_APP_BACKEND_URL + url), "_blank");
  };

  const approve = () => {
    if (!canApprove) return toast.error("Pick at least one hall and a date range with working days");
    if (conflicts.length && !window.confirm(`These dates overlap ${conflicts.length} existing booking(s). Approve anyway?`)) return;
    onUpdate({
      status: "approved",
      assigned_halls: halls,
      assigned_start_date: start,
      assigned_end_date: end,
      override_fee_inr: isOverride ? finalFee : 0,
      fee_note: feeNote,
      admin_notes: notes,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-[820px] bg-cream overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="app-drawer">
        <div className="sticky top-0 bg-cream border-b border-soft p-6 flex items-center justify-between">
          <div>
            <div className="overline">{app.reference}</div>
            <div className="font-serif text-2xl mt-1">{app.artist_name}</div>
            {app.exhibition_title && <div className="text-sm italic text-muted2 mt-1">&ldquo;{app.exhibition_title}&rdquo;</div>}
          </div>
          <button onClick={onClose} data-testid="drawer-close"><X size={22}/></button>
        </div>

        <div className="p-6 space-y-10">

          {/* Contact + files */}
          <section className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              {app.artist_photo_url ? (
                <img src={process.env.REACT_APP_BACKEND_URL + app.artist_photo_url} alt="artist" className="w-full aspect-square object-cover border border-soft"/>
              ) : (
                <div className="w-full aspect-square bg-cream-2 border border-soft flex items-center justify-center text-muted2 text-xs">No photo</div>
              )}
            </div>
            <div className="md:col-span-2 text-sm space-y-2">
              <div><span className="text-muted2">Email</span> · <a href={`mailto:${app.email}`} className="link-hover">{app.email}</a></div>
              <div><span className="text-muted2">Phone</span> · {app.phone}</div>
              {app.whatsapp && <div><span className="text-muted2">WhatsApp</span> · {app.whatsapp}</div>}
              {app.website && <div><span className="text-muted2">Website</span> · <a href={app.website} target="_blank" rel="noreferrer" className="link-hover">{app.website}</a></div>}
              {app.instagram && <div><span className="text-muted2">Instagram</span> · {app.instagram}</div>}
              <div className="pt-3">
                {app.cv_url && (
                  <button onClick={() => openFile(app.cv_url)} className="text-xs inline-flex items-center gap-1 link-hover" data-testid="open-cv">
                    <ExternalLink size={12}/> {app.cv_filename || "Open CV"}
                  </button>
                )}
              </div>
            </div>
          </section>

          <section>
            <div className="overline mb-2">Artist statement</div>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{app.artist_statement}</p>
            <div className="text-xs text-muted2 mt-3">Expected works to display · {app.expected_works || "—"}</div>
          </section>

          {/* Preferred */}
          <section>
            <div className="overline mb-2">Preferred</div>
            <div className="text-sm">{options[app.preferred_option]?.label} · {formatINR(options[app.preferred_option]?.total)} (5 days)</div>
            <ul className="mt-2 text-sm space-y-1">
              {(app.preferred_slot_ids || []).map((sid, i) => (
                <li key={sid} className="text-muted2">{i + 1}. {formatSlot(sid, slots)}</li>
              ))}
            </ul>
          </section>

          {/* Artworks */}
          <section>
            <div className="overline mb-3">Artworks ({app.artworks?.length || 0})</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {(app.artworks || []).map((a, i) => (
                <div key={i} className="border border-soft p-3">
                  {a.image_url && (
                    <button onClick={() => openFile(a.image_url)} className="block w-full aspect-square overflow-hidden mb-2">
                      <img src={process.env.REACT_APP_BACKEND_URL + a.image_url} alt={a.title} className="w-full h-full object-cover hover:scale-105 transition"/>
                    </button>
                  )}
                  <div className="font-serif text-lg">{a.title || "Untitled"}</div>
                  <div className="text-xs text-muted2 mt-1">{a.medium}</div>
                  <div className="text-xs text-muted2">{a.size}</div>
                  <div className="text-xs">{a.price}</div>
                </div>
              ))}
            </div>
          </section>

          {app.additional_notes && (
            <section>
              <div className="overline mb-2">Notes from artist</div>
              <p className="text-sm whitespace-pre-wrap">{app.additional_notes}</p>
            </section>
          )}

          {/* Decision panel */}
          <section className="border border-soft p-5 bg-white">
            <div className="overline mb-4">Decision</div>

            <ApprovalSchedule app={app} rates={rates} halls={halls} setHalls={setHalls} start={start} setStart={setStart} end={end} setEnd={setEnd} onConflicts={setConflicts} />

            <div className="mt-4 text-xs text-muted2" data-testid="decision-calc">
              {halls.length ? HALL_KEYS.filter((h) => halls.includes(h)).map((h) => `Gallery ${h} ${formatINR(rates[h] || 0)}`).join(" + ") : "No halls"} = <strong className="text-charcoal">{formatINR(perDay)}/day</strong>
              {" "}× <strong className="text-charcoal">{workingDays} working days</strong> (Mon &amp; Tue excluded) = <strong className="text-charcoal">{formatINR(defaultFee)}</strong>
            </div>

            <div className="mt-5 grid md:grid-cols-2 gap-4">
              <label className="block text-xs">
                <div className="mb-1 text-muted2 uppercase tracking-widest flex justify-between">
                  <span>Fee (₹) — customer-facing</span>
                  {isOverride && <span style={{color:"#C05C42"}}>Override</span>}
                </div>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={feeInput}
                  onChange={(e) => { setFeeInput(e.target.value); setFeeTouched(true); }}
                  className="w-full bg-transparent border-b border-soft py-2 text-sm font-serif text-lg"
                  placeholder={String(defaultFee)}
                  data-testid="decision-fee"
                />
                <div className="text-[11px] text-muted2 mt-1">
                  Calculated: {formatINR(defaultFee)}. Type a different amount to override.
                  {isOverride && <> Charging <strong>{formatINR(finalFee)}</strong>.</>}
                </div>
              </label>

              <label className="block text-xs">
                <div className="mb-1 text-muted2 uppercase tracking-widest">Fee note (shown to artist)</div>
                <input
                  type="text"
                  value={feeNote}
                  onChange={(e) => setFeeNote(e.target.value)}
                  placeholder="e.g. 10 days − 1 public holiday (25 Dec) = 9 days at ₹5,000/day"
                  className="w-full bg-transparent border-b border-soft py-2 text-sm"
                  maxLength={140}
                  data-testid="decision-fee-note"
                />
                <div className="text-[11px] text-muted2 mt-1">Optional. Recommended when the fee differs from the default.</div>
              </label>
            </div>

            <label className="block text-xs mt-5">
              <div className="mb-1 text-muted2 uppercase tracking-widest">Admin notes (internal only)</div>
              <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full bg-transparent border border-soft p-3 text-sm" data-testid="decision-notes"/>
            </label>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={approve}
                disabled={!canApprove}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="decision-approve"
              ><Check size={14}/> {app.status === "approved" ? "Re-approve & re-send email" : "Approve & email artist"}</button>
              {app.status === "approved" && (
                <button
                  onClick={() => onUpdate({ status: "paid", admin_notes: notes })}
                  className="btn-ghost"
                  style={{borderColor:"#4A5D4E", color:"#4A5D4E"}}
                  data-testid="decision-mark-paid"
                  title="For cash payments, bank transfers or comp shows — marks the application paid and adds it to Upcoming Events"
                ><CheckCircle2 size={14}/> Mark as paid</button>
              )}
              {app.status === "paid" && (
                <button
                  onClick={() => onUpdate({ status: "approved", admin_notes: notes })}
                  className="btn-ghost"
                  style={{borderColor:"#8A6D3B", color:"#8A6D3B"}}
                  data-testid="decision-revert-paid"
                  title="Un-mark this as paid — it will disappear from the public listing"
                ><Undo2 size={14}/> Revert to approved</button>
              )}
              <button onClick={() => onUpdate({ status: "shortlisted", admin_notes: notes })} className="btn-ghost" data-testid="decision-shortlist">Shortlist</button>
              <button onClick={() => onUpdate({ status: "rejected", admin_notes: notes })} className="btn-ghost" style={{borderColor:"#8B2A2A",color:"#8B2A2A"}} data-testid="decision-reject"><Ban size={14}/> Reject</button>
            </div>

            <div className="mt-4 text-xs text-muted2">
              Current status: <strong style={{color: STATUS_COLORS[app.status]}}>{app.status}</strong>
              {app.status === "paid" && <> · <span style={{color:"#4A5D4E"}}>Listed in Upcoming Events</span>{app.event_slug && <> · <a href={`/events/${app.event_slug}`} target="_blank" rel="noreferrer" className="link-hover" style={{color:"#C05C42"}} data-testid="decision-view-event">View event ↗</a></>}</>}
              {app.status === "approved" && <> · <span style={{color:"#8A6D3B"}}>Awaiting payment — not yet public</span></>}
              {app.payment_due_date && <> · Payment due by <strong>{app.payment_due_date}</strong></>}
              {app.payment_link_url && <> · <a href={app.payment_link_url} target="_blank" rel="noreferrer" className="link-hover" style={{color:"#C05C42"}}>Open payment link ↗</a></>}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
